import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../supabase";
import { useAuth } from "../../contexts/AuthContext";
import { ensureListingCreditAvailable, NO_LISTING_CREDITS_ERROR } from "../../services/listingPurchases";
import Select from "react-select";
import countryList from "react-select-country-list";
import { State } from "country-state-city";
import { specialties, roleCategories } from "../../constants/specialties";
import JobListingPurchasePanel from "../../components/employer/JobListingPurchasePanel";
import useListingCredits from "../../hooks/useListingCredits";

// Language options for dropdown
const languages = [
    "English", "Afrikaans", "Zulu", "Xhosa", "French", "German", "Spanish", "Portuguese",
    "Arabic", "Mandarin", "Hindi", "Swahili", "Yoruba", "Igbo", "Hausa", "Italian",
    "Dutch", "Russian", "Japanese", "Korean", "Turkish", "Polish", "Greek", "Hebrew",
    "Urdu", "Bengali", "Tamil", "Telugu", "Marathi", "Gujarati", "Punjabi", "Malayalam",
    "Kannada", "Odia", "Assamese", "Nepali", "Sinhala", "Thai", "Vietnamese", "Indonesian",
    "Malay", "Tagalog", "Filipino", "Swedish", "Norwegian", "Danish", "Finnish", "Czech",
    "Hungarian", "Romanian", "Bulgarian", "Croatian", "Serbian", "Slovak", "Slovenian",
    "Lithuanian", "Latvian", "Estonian", "Ukrainian", "Persian", "Pashto", "Kurdish",
    "Amharic", "Somali", "Fulani", "Wolof", "Kinyarwanda", "Luganda", "Setswana", "Sesotho",
    "Tswana", "Venda", "Tsonga", "Ndebele", "Swati", "Northern Sotho", "Southern Sotho"
];

// Common currencies with codes
const currencies = [
    { code: "ZAR", symbol: "R", name: "South African Rand" },
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "EUR", symbol: "€", name: "Euro" },
    { code: "GBP", symbol: "£", name: "British Pound" },
    { code: "AUD", symbol: "A$", name: "Australian Dollar" },
    { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
    { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
    { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
    { code: "GHS", symbol: "₵", name: "Ghanaian Cedi" },
    { code: "INR", symbol: "₹", name: "Indian Rupee" },
    { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
    { code: "CHF", symbol: "Fr", name: "Swiss Franc" },
];

// Salary periods
const salaryPeriods = [
    { value: "yearly", label: "Yearly" },
    { value: "monthly", label: "Monthly" },
    { value: "weekly", label: "Weekly" },
    { value: "hourly", label: "Hourly" },
];

const EmployerJobs = () => {
    const { user } = useAuth();
    const { credits, refresh: refreshCredits } = useListingCredits();
    const navigate = useNavigate();
    const location = useLocation();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [applicationsCountByJob, setApplicationsCountByJob] = useState({});
    const jobsTableGridTemplate = "60px 3fr 1.5fr 1.5fr 2fr 2.5fr 1fr";
    const [showPromotionModal, setShowPromotionModal] = useState(false);
    const [selectedPromotionPlan, setSelectedPromotionPlan] = useState('free');
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showNewJobModal, setShowNewJobModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [savingNewJob, setSavingNewJob] = useState(false);
    const [jobFormStep, setJobFormStep] = useState(2); // Step 2 = Job description, Step 3 = Requirements
    const [newJobForm, setNewJobForm] = useState({
        // Step 2: Job description fields
        jobTitle: "",
        specialty: "",
        roleDescription: "",
        preferredCandidate: "",
        dutiesAndResponsibilities: "",
        employmentType: "",
        workPreference: "",
        country: "",
        countryCode: "",
        province: "",
        languageRequirement: [],
        locationOrLicenceRestrictions: null,
        locationOrLicenceRestrictionsText: "",
        // Step 3: Requirements fields
        requirementsSpecialty: "",
        experienceLevel: "",
        yearsOfExperience: "",
        budgetForRole: "",
        salaryCurrency: "ZAR",
        salaryPeriod: "yearly",
        requiredSkills: [],
        otherPreferences: "",
        screeningQuestions: false,
        driversLicenseRequired: false,
        driversLicenseAnswer: null,
        visaSponsorshipOffered: false,
        visaSponsorshipAnswer: null,
        about: "",
        is_dev_job: false
    });
    
    // Additional state for step 3 requirements
    const [skillInput, setSkillInput] = useState("");
    const [currentQuestion, setCurrentQuestion] = useState("");
    const [currentQuestionType, setCurrentQuestionType] = useState("multiple-choice"); // "multiple-choice" or "text"
    const [currentOptions, setCurrentOptions] = useState([""]);
    const [screeningQuestionsList, setScreeningQuestionsList] = useState([]);
    const [driversLicenseAnswer, setDriversLicenseAnswer] = useState(null);
    const [visaSponsorshipAnswer, setVisaSponsorshipAnswer] = useState(null);

    useEffect(() => {
        if (user) {
            fetchJobs();
        }
    }, [user]);

    // Show promotion modal if navigated with state from another page
    useEffect(() => {
        if (location.state?.showPromotionModal) {
            setShowPromotionModal(true);
            // Clear the state so it doesn't show again on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    // Auto-populate screening questions from onboarding when opening new job modal
    useEffect(() => {
        if (showNewJobModal && user && !newJobForm.screeningQuestions) {
            const onboardingQuestions = user?.user_metadata?.employer_profile?.requirements?.screeningQuestionsList;
            const onboardingEnabled = user?.user_metadata?.employer_profile?.requirements?.screeningQuestions;
            
            if (onboardingEnabled && onboardingQuestions && Array.isArray(onboardingQuestions) && onboardingQuestions.length > 0) {
                setNewJobForm((p) => ({ ...p, screeningQuestions: true }));
                setScreeningQuestionsList(onboardingQuestions);
            }
        }
    }, [showNewJobModal, user]);

    const fetchJobs = async () => {
        if (!user) return;
        let jobs = [];
        
        try {
            setLoading(true);

            if (user?.email.includes('@fourminutemedicine.com')) {
                try {
                    jobs = await fetchAllIncludingDevModeJobs();
                } catch (error) {
                    console.error("Error fetching dev mode jobs:", error);
                    throw error;
                }   
            } else {
                try {
                    jobs = await fetchAllExcludingDevModeJobs();
                } catch (error) {
                    console.error("Error fetching non-dev mode jobs:", error);
                    throw error;
                }
            }

            // Fetch all applications for all jobs in parallel (fixes N+1 query problem)
            const jobIds = (jobs || []).map(job => job.id);
            
            // Fetch all applications for all jobs at once
            const { data: allApplications, error: allAppsError } = await supabase
                .from('applications')
                .select('id, status, user_id, job_listing_id')
                .in('job_listing_id', jobIds);

            if (allAppsError) {
                // Error fetching applications
            }

            // Group applications by job_listing_id
            const applicationsByJob = {};
            (allApplications || []).forEach(app => {
                if (!applicationsByJob[app.job_listing_id]) {
                    applicationsByJob[app.job_listing_id] = [];
                }
                applicationsByJob[app.job_listing_id].push(app);
            });

            // Collect all unique user IDs from all applications
            const allUserIds = [...new Set((allApplications || []).map(app => app.user_id).filter(Boolean))];

            // Batch fetch all candidate profiles in a single query (fixes N+1 problem)
            const profilesMap = {};
            if (allUserIds.length > 0) {
                const { data: allProfiles, error: profilesError } = await supabase
                    .from('users_profiles')
                    .select('profileid, profile_image_url, firstname, lastname')
                    .in('profileid', allUserIds);

                if (!profilesError && allProfiles) {
                    allProfiles.forEach(profile => {
                        let profileImageUrl = null;
                        if (profile.profile_image_url) {
                            if (profile.profile_image_url.startsWith('http')) {
                                profileImageUrl = profile.profile_image_url;
                            } else {
                                const { data: imageData } = supabase.storage
                                    .from('profile-images')
                                    .getPublicUrl(profile.profile_image_url);
                                profileImageUrl = imageData.publicUrl;
                            }
                        }
                        profilesMap[profile.profileid] = {
                            imageUrl: profileImageUrl,
                            initials: profile.firstname && profile.lastname 
                                ? `${profile.firstname.charAt(0)}${profile.lastname.charAt(0)}`.toUpperCase()
                                : 'U'
                        };
                    });
                }
            }

            // Process each job with pre-fetched data
            const jobsWithApplications = (jobs || []).map((job) => {
                const applications = (applicationsByJob[job.id] || []).slice(0, 4); // Limit to first 4 for display
                
                // Build candidate images from pre-fetched profiles map
                const candidateImages = applications
                    .map(app => profilesMap[app.user_id])
                    .filter(Boolean); // Remove any undefined entries

                return {
                    ...job,
                    candidates: applications.length,
                    applications: applications,
                    candidateImages: candidateImages
                };
            });

            // Set jobs with candidateImages attached directly to each job object
            setJobs(jobsWithApplications);

            // Calculate and set application counts by job for display
            const counts = {};
            Object.keys(applicationsByJob).forEach(jobId => {
                counts[jobId] = applicationsByJob[jobId].length;
            });
            setApplicationsCountByJob(counts);
        } catch (err) {
            setError("Failed to load job posts");
        } finally {
            setLoading(false);
        }
    };

    const fetchAllIncludingDevModeJobs = async () => {
        const { data: jobs, error: jobsError } = await supabase
            .from('internal_job_listings')
            .select('*')
            .eq('employer_id', user.id)
            .order('created_at', { ascending: false });
        return jobs;
    };

    const fetchAllExcludingDevModeJobs = async () => {
        const { data: jobs, error: jobsError } = await supabase
            .from('internal_job_listings')
            .select('*')
            .eq('employer_id', user.id)
            .eq('is_dev_job', false)
            .order('created_at', { ascending: false });
        return jobs;
    };
    
    const handleEditJob = (jobId) => {
        // Navigate to edit job page or open edit modal
    };

    const handleDeleteJob = async (jobId) => {
        if (!window.confirm("Are you sure you want to delete this job post?")) {
            return;
        }

        try {
            const { error } = await supabase
                .from('internal_job_listings')
                .delete()
                .eq('id', jobId)
                .eq('employer_id', user.id);

            if (error) throw error;
            
            // Refresh the jobs list
            fetchJobs();
        } catch (err) {
            alert("Failed to delete job post. Please try again.");
        }
    };

    const handleViewApplications = (jobId) => {
        // Navigate to applications page for this job
        navigate(`/employer/candidates?jobId=${jobId}`);
    };

    const handleJobClick = (jobId) => {
        // Navigate to job description page
        navigate(`/employer/job-detail/${jobId}`);
    };

    // Helper functions for step 3 requirements
    const addSkill = (skill) => {
        if (skill.trim() && !newJobForm.requiredSkills.includes(skill.trim())) {
            setNewJobForm((p) => ({
                ...p,
                requiredSkills: [...p.requiredSkills, skill.trim()]
            }));
            setSkillInput("");
        }
    };

    const removeSkill = (skillIndex) => {
        setNewJobForm((p) => ({
            ...p,
            requiredSkills: p.requiredSkills.filter((_, i) => i !== skillIndex)
        }));
    };

    const addScreeningOption = () => {
        setCurrentOptions([...currentOptions, ""]);
    };

    const updateScreeningOption = (index, value) => {
        const updated = [...currentOptions];
        updated[index] = value;
        setCurrentOptions(updated);
    };

    const removeScreeningOption = (index) => {
        setCurrentOptions(currentOptions.filter((_, i) => i !== index));
    };

    const addScreeningQuestion = () => {
        if (currentQuestion.trim()) {
            if (currentQuestionType === "text") {
                const question = {
                    question: currentQuestion.trim(),
                    type: "text"
                };
                setScreeningQuestionsList([...screeningQuestionsList, question]);
                setCurrentQuestion("");
                setCurrentQuestionType("multiple-choice");
                setCurrentOptions([""]);
            } else {
                if (currentOptions.some(opt => opt.trim())) {
                    const question = {
                        question: currentQuestion.trim(),
                        type: "multiple-choice",
                        options: currentOptions.filter(opt => opt.trim())
                    };
                    setScreeningQuestionsList([...screeningQuestionsList, question]);
                    setCurrentQuestion("");
                    setCurrentQuestionType("multiple-choice");
                    setCurrentOptions([""]);
                }
            }
        }
    };

    const removeScreeningQuestion = (index) => {
        setScreeningQuestionsList(screeningQuestionsList.filter((_, i) => i !== index));
    };

    useEffect(() => {
        if (showPromotionModal) {
            setSelectedPromotionPlan('free');
        }
    }, [showPromotionModal]);

    const handlePromotionAction = (promote) => {
        setShowPromotionModal(false);
        if (promote) {
            setShowPurchaseModal(true);
        }
    };

    const handleClosePurchaseModal = () => {
        setShowPurchaseModal(false);
    };

    const handleNextStep = () => {
        if (jobFormStep === 2) {
            // Validate step 2 required fields
            if (!newJobForm.jobTitle || !newJobForm.specialty || !newJobForm.employmentType || !newJobForm.workPreference) {
                alert("Please fill in all required fields (Job Title, Role category, Employment Type, Work preference)");
                return;
            }
            setJobFormStep(3);
        }
    };

    const handleBackStep = () => {
        if (jobFormStep === 3) {
            setJobFormStep(2);
        }
    };

    const createJobListing = async () => {
        if (!user) {
            alert("You must be logged in as an employer to create a job.");
            return;
        }
        if (!newJobForm.jobTitle || !newJobForm.specialty) {
            alert("Please fill at least Job Title and Role category.");
            return;
        }
        setSavingNewJob(true);
        try {
            await ensureListingCreditAvailable(user.id, user.email);

            const employerProfile = user?.user_metadata?.employer_profile || {};
            const companyInfo = employerProfile.companyInfo || {};

            // Build location string from country/province
            const locationParts = [];
            if (newJobForm.province) locationParts.push(newJobForm.province);
            if (newJobForm.country) locationParts.push(newJobForm.country);
            const location = locationParts.join(", ") || "";

            // Include any unsaved screening question that's currently being typed
            let finalScreeningQuestionsList = [...screeningQuestionsList];
            if (currentQuestion.trim()) {
                if (currentQuestionType === "text") {
                    const pendingQuestion = {
                        question: currentQuestion.trim(),
                        type: "text"
                    };
                    finalScreeningQuestionsList.push(pendingQuestion);
                } else if (currentOptions.some(opt => opt.trim())) {
                    const pendingQuestion = {
                        question: currentQuestion.trim(),
                        type: "multiple-choice",
                        options: currentOptions.filter(opt => opt.trim())
                    };
                    finalScreeningQuestionsList.push(pendingQuestion);
                }
            }

            const { data, error } = await supabase
                .from('internal_job_listings')
                .insert({
                    employer_id: user.id,
                    title: newJobForm.jobTitle,
                    company_name: companyInfo.companyName || companyInfo.companyname || "",
                    location: location,
                    employment_type: newJobForm.employmentType,
                    work_preference: newJobForm.workPreference,
                    specialty: newJobForm.specialty,
                    requires_license: newJobForm.locationOrLicenceRestrictions === 'yes',
                    sponsorship_available: newJobForm.visaSponsorshipOffered,
                    job_description: newJobForm.about || newJobForm.roleDescription,
                    contact_first_name: companyInfo.firstName || "",
                    contact_last_name: companyInfo.lastName || "",
                    contact_email: companyInfo.workEmail || "",
                    company_url: companyInfo.companyUrl || "",
                    industry: companyInfo.industry || "",
                    company_description: companyInfo.about || "",
                    logo_filename: companyInfo.logoFileName || null,
                    social_media_links: companyInfo.socialMediaLinks || null,
                    role_description: newJobForm.roleDescription,
                    preferred_candidate: newJobForm.preferredCandidate,
                    duties_and_responsibilities: newJobForm.dutiesAndResponsibilities,
                    requirements_specialty: newJobForm.requirementsSpecialty || newJobForm.specialty,
                    experience_level: newJobForm.experienceLevel || null,
                    years_of_experience: newJobForm.yearsOfExperience || null,
                    budget_for_role: newJobForm.budgetForRole || null,
                    salary_currency: newJobForm.salaryCurrency || "ZAR",
                    salary_period: newJobForm.salaryPeriod || "yearly",
                    salary_range: newJobForm.budgetForRole || null, // Also save to salary_range for display compatibility
                    required_skills: Array.isArray(newJobForm.requiredSkills) ? newJobForm.requiredSkills : [],
                    other_preferences: newJobForm.otherPreferences,
                    country: newJobForm.country,
                    country_code: newJobForm.countryCode,
                    province: newJobForm.province,
                    hiring_restrictions: newJobForm.locationOrLicenceRestrictionsText || "",
                    screening_questions: newJobForm.screeningQuestions,
                    screening_questions_list: finalScreeningQuestionsList.length > 0 ? finalScreeningQuestionsList : null,
                    drivers_license_required: newJobForm.driversLicenseRequired,
                    drivers_license_answer: driversLicenseAnswer,
                    visa_sponsorship_offered: newJobForm.visaSponsorshipOffered,
                    visa_sponsorship_answer: visaSponsorshipAnswer,
                    status: 'published',
                    is_dev_job: newJobForm.is_dev_job || false
                })
                .select('id')
                .single();

            if (error) throw error;

            // Refresh job list
            setShowNewJobModal(false);
            setJobFormStep(2);
            // Reset form
            setNewJobForm({
                jobTitle: "", specialty: "", roleDescription: "", preferredCandidate: "", dutiesAndResponsibilities: "",
                employmentType: "", workPreference: "", country: "", countryCode: "", province: "",
                languageRequirement: [], locationOrLicenceRestrictions: null, locationOrLicenceRestrictionsText: "",
                requirementsSpecialty: "", experienceLevel: "", yearsOfExperience: "", budgetForRole: "",
                salaryCurrency: "ZAR", salaryPeriod: "yearly",
                requiredSkills: [], otherPreferences: "", screeningQuestions: false, driversLicenseRequired: false,
                driversLicenseAnswer: null, visaSponsorshipOffered: false, visaSponsorshipAnswer: null, about: "",
                is_dev_job: false
            });
            setSkillInput("");
            setCurrentQuestion("");
            setCurrentOptions([""]);
            setScreeningQuestionsList([]);
            setDriversLicenseAnswer(null);
            setVisaSponsorshipAnswer(null);
            fetchJobs();
            // Refresh credits after job creation so count updates immediately
            await refreshCredits();
            window.dispatchEvent(new Event('refreshCredits'));
            // Temporarily commented out - promotion modal after job creation
            // setShowPromotionModal(true);
        } catch (err) {
            if (err?.code === NO_LISTING_CREDITS_ERROR) {
                // Show error message to user
                alert(err.message || 'You have reached your job listing limit. Please upgrade to post more jobs.');
                // Show upgrade modal for all users who run out of listings (free or paid)
                setShowUpgradeModal(true);
                return;
            }
            alert('Failed to create job listing. Please try again.');
        } finally {
            setSavingNewJob(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) return '1 day ago';
        if (diffDays < 7) return `${diffDays} days ago`;
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Helper function to get flag emoji based on location
    const getLocationFlag = (location) => {
        if (!location) return '🌍';
        
        const locationLower = location.toLowerCase();
        
        // Country/region mappings
        const flagMap = {
            'singapore': '🇸🇬',
            'malaysia': '🇲🇾',
            'thailand': '🇹🇭',
            'indonesia': '🇮🇩',
            'philippines': '🇵🇭',
            'vietnam': '🇻🇳',
            'cambodia': '🇰🇭',
            'laos': '🇱🇦',
            'myanmar': '🇲🇲',
            'brunei': '🇧🇳',
            'australia': '🇦🇺',
            'new zealand': '🇳🇿',
            'japan': '🇯🇵',
            'south korea': '🇰🇷',
            'china': '🇨🇳',
            'hong kong': '🇭🇰',
            'taiwan': '🇹🇼',
            'india': '🇮🇳',
            'bangladesh': '🇧🇩',
            'sri lanka': '🇱🇰',
            'pakistan': '🇵🇰',
            'united states': '🇺🇸',
            'usa': '🇺🇸',
            'canada': '🇨🇦',
            'united kingdom': '🇬🇧',
            'uk': '🇬🇧',
            'germany': '🇩🇪',
            'france': '🇫🇷',
            'italy': '🇮🇹',
            'spain': '🇪🇸',
            'netherlands': '🇳🇱',
            'switzerland': '🇨🇭',
            'sweden': '🇸🇪',
            'norway': '🇳🇴',
            'denmark': '🇩🇰',
            'finland': '🇫🇮',
            'ireland': '🇮🇪',
            'austria': '🇦🇹',
            'belgium': '🇧🇪',
            'portugal': '🇵🇹',
            'greece': '🇬🇷',
            'poland': '🇵🇱',
            'czech republic': '🇨🇿',
            'hungary': '🇭🇺',
            'romania': '🇷🇴',
            'bulgaria': '🇧🇬',
            'croatia': '🇭🇷',
            'slovenia': '🇸🇮',
            'slovakia': '🇸🇰',
            'estonia': '🇪🇪',
            'latvia': '🇱🇻',
            'lithuania': '🇱🇹',
            'russia': '🇷🇺',
            'ukraine': '🇺🇦',
            'turkey': '🇹🇷',
            'israel': '🇮🇱',
            'saudi arabia': '🇸🇦',
            'uae': '🇦🇪',
            'united arab emirates': '🇦🇪',
            'qatar': '🇶🇦',
            'kuwait': '🇰🇼',
            'bahrain': '🇧🇭',
            'oman': '🇴🇲',
            'jordan': '🇯🇴',
            'lebanon': '🇱🇧',
            'egypt': '🇪🇬',
            'south africa': '🇿🇦',
            'cape town': '🇿🇦',
            'johannesburg': '🇿🇦',
            'durban': '🇿🇦',
            'pretoria': '🇿🇦',
            'port elizabeth': '🇿🇦',
            'bloemfontein': '🇿🇦',
            'nigeria': '🇳🇬',
            'kenya': '🇰🇪',
            'ghana': '🇬🇭',
            'morocco': '🇲🇦',
            'tunisia': '🇹🇳',
            'algeria': '🇩🇿',
            'brazil': '🇧🇷',
            'argentina': '🇦🇷',
            'chile': '🇨🇱',
            'colombia': '🇨🇴',
            'peru': '🇵🇪',
            'mexico': '🇲🇽',
            'venezuela': '🇻🇪',
            'ecuador': '🇪🇨',
            'uruguay': '🇺🇾',
            'paraguay': '🇵🇾',
            'bolivia': '🇧🇴',
            'guyana': '🇬🇾',
            'suriname': '🇸🇷',
            'french guiana': '🇬🇫'
        };

        // Check for exact matches first
        if (flagMap[locationLower]) {
            return flagMap[locationLower];
        }

        // Check for partial matches (e.g., "New York, USA" should match "USA")
        for (const [country, flag] of Object.entries(flagMap)) {
            if (locationLower.includes(country)) {
                return flag;
            }
        }

        // Default to world flag if no match found
        return '🌍';
    };

    const getFilteredJobs = () => {
        let filtered = jobs;
        
        // Filter by tab
        // "all" tab shows only published jobs (excludes draft and archived)
        // Draft jobs only appear in "draft" tab
        // Archived jobs only appear in "archived" tab
        if (activeTab === 'all') {
            filtered = filtered.filter(job => job.status === 'published');
        } else {
            filtered = filtered.filter(job => {
                if (activeTab === 'active') return job.status === 'published';
                if (activeTab === 'draft') return job.status === 'draft';
                if (activeTab === 'archived') return job.status === 'closed';
                return true;
            });
        }
        
        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(job => 
                job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                job.company_name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        return filtered;
    };

    const handleSelectJob = (jobId) => {
        setSelectedJobs(prev => 
            prev.includes(jobId) 
                ? prev.filter(id => id !== jobId)
                : [...prev, jobId]
        );
    };

    const handleSelectAll = () => {
        const filteredJobs = getFilteredJobs();
        if (selectedJobs.length === filteredJobs.length) {
            setSelectedJobs([]);
        } else {
            setSelectedJobs(filteredJobs.map(job => job.id));
        }
    };

    // Update job status in bulk
    const updateJobStatus = async (newStatus) => {
        if (selectedJobs.length === 0) return;

        // Map UI status to database status
        const statusMap = {
            'Posted': 'published',
            'Active': 'published',
            'Draft': 'draft',
            'Archived': 'closed'
        };
        const dbStatus = statusMap[newStatus] || (newStatus === 'Posted' ? 'published' : newStatus.toLowerCase());

        try {
            const { error } = await supabase
                .from('internal_job_listings')
                .update({ status: dbStatus })
                .in('id', selectedJobs)
                .eq('employer_id', user.id);

            if (error) throw error;

            // Refresh jobs list
            fetchJobs();
            
            // Clear selection
            setSelectedJobs([]);
            
            // Switch to the appropriate tab based on the new status
            if (newStatus === 'Archived') {
                setActiveTab('archived');
            } else if (newStatus === 'Draft') {
                setActiveTab('draft');
            } else if (newStatus === 'Posted') {
                setActiveTab('active');
            }
            
            // Show success message
            alert(`Successfully moved ${selectedJobs.length} job(s) to ${newStatus}`);
        } catch (error) {
            alert('Failed to update job status. Please try again.');
        }
    };

    // Delete jobs in bulk
    const deleteJobs = async () => {
        if (selectedJobs.length === 0) return;

        const confirmMessage = `Are you sure you want to delete ${selectedJobs.length} job${selectedJobs.length !== 1 ? 's' : ''}? This action cannot be undone.`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            const { error } = await supabase
                .from('internal_job_listings')
                .delete()
                .in('id', selectedJobs)
                .eq('employer_id', user.id);

            if (error) throw error;

            // Refresh jobs list
            fetchJobs();
            
            // Clear selection
            setSelectedJobs([]);
            
            // Show success message
            alert(`Successfully deleted ${selectedJobs.length} job${selectedJobs.length !== 1 ? 's' : ''}`);
        } catch (error) {
            alert('Failed to delete jobs. Please try again.');
        }
    };

    // Clear selection when tab changes
    useEffect(() => {
        setSelectedJobs([]);
    }, [activeTab]);

    const getStatusBadge = (status) => {
        const statusConfig = {
            published: { 
                color: 'bg-green-100 text-green-800', 
                label: 'Posted'
            },
            draft: { 
                color: 'bg-yellow-100 text-yellow-800', 
                label: 'Draft'
            },
            closed: { 
                color: 'bg-red-100 text-red-800', 
                label: 'Archived'
            }
        };
        
        const config = statusConfig[status] || statusConfig.draft;
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
                {config.label}
            </span>
        );
    };

    const getTypeBadge = (employmentType) => {
        const typeConfig = {
            'Full-time': 'bg-gray-100 text-gray-800',
            'Part-time': 'bg-gray-100 text-gray-800',
            'Contract': 'bg-gray-100 text-gray-800',
            'Internship': 'bg-gray-100 text-gray-800'
        };
        
        const color = typeConfig[employmentType] || 'bg-gray-100 text-gray-800';
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${color}`}>
                {employmentType}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="mb-8">
                        <div className="h-8 bg-gray-200 rounded-lg w-48 mb-4 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-96 animate-pulse"></div>
                    </div>
                    <div className="grid gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex-1">
                                        <div className="h-6 bg-gray-200 rounded w-1/3 mb-3"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="h-8 bg-gray-200 rounded w-20"></div>
                                        <div className="h-8 bg-gray-200 rounded w-16"></div>
                                        <div className="h-8 bg-gray-200 rounded w-16"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <>
            <style>{`
                @media (max-width: 768px) {
                    .employer-jobs-h1 {
                        font-size: 24px !important;
                    }
                    .employer-jobs-h2 {
                        font-size: 24px !important;
                    }
                }
            `}</style>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2 employer-jobs-h1">My Job Posts</h1>
                        <p className="text-gray-600">Manage and track your job postings</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Job Posts</h3>
                        <p className="text-red-600 mb-6">{error}</p>
                        <button 
                            onClick={fetchJobs}
                            className="inline-flex items-center px-6 py-3 bg-red-600 text-white font-medium rounded-full hover:bg-red-700 transition-colors shadow-sm"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
            </>
        );
    }

    const filteredJobs = getFilteredJobs();

    return (
        <>
        <style>{`
            @media (max-width: 768px) {
                .employer-jobs-h1 {
                    font-size: 24px !important;
                }
                .employer-jobs-h2 {
                    font-size: 24px !important;
                }
            }
            @media (max-width: 1024px) {
                .employer-jobs-container {
                    padding: 1rem !important;
                }
                .employer-jobs-tabs {
                    gap: 0.5rem !important;
                    overflow-x: auto !important;
                    border-bottom-width: 0 !important;
                }
                .employer-jobs-tabs button {
                    font-size: 0.875rem !important;
                    padding-bottom: 0.25rem !important;
                    border-bottom-width: 2px !important;
                }
                .employer-jobs-controls {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 0.75rem !important;
                }
                .employer-jobs-controls-inner {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 0.75rem !important;
                }
                .employer-jobs-search input {
                    width: 100% !important;
                    padding: 0.75rem 0.75rem 0.75rem 2.5rem !important;
                    font-size: 0.875rem !important;
                }
                .employer-jobs-controls button {
                    width: 100% !important;
                    justify-content: center !important;
                }
                .employer-jobs-new-button {
                    align-self: flex-start !important;
                    margin-left: 0 !important;
                    justify-content: flex-start !important;
                }
                .employer-jobs-filter-button {
                    display: none !important;
                }
                .employer-jobs-bulk {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.5rem !important;
                }
                .employer-jobs-bulk-actions {
                    width: 100% !important;
                    flex-wrap: wrap !important;
                    gap: 0.5rem !important;
                }
                .employer-jobs-table {
                    border: none !important;
                }
                .employer-jobs-table-header {
                    display: none !important;
                }
                .employer-jobs-table-row {
                    padding: 1rem !important;
                    border-bottom: 1px solid #e5e7eb !important;
                }
                .employer-jobs-table-row-grid {
                    display: flex !important;
                    flex-direction: column !important;
                    gap: 0.75rem !important;
                }
                .employer-jobs-table-row-grid > div {
                    width: 100% !important;
                }
                .employer-jobs-row-meta {
                    display: flex !important;
                    gap: 0.5rem !important;
                    flex-wrap: wrap !important;
                }
                .employer-jobs-applicants {
                    justify-content: space-between !important;
                }
                .employer-jobs-empty {
                    padding: 2rem !important;
                }
                .employer-jobs-modal-overlay {
                    padding: 1rem !important;
                    align-items: flex-start !important;
                }
                .employer-jobs-modal {
                    width: 100% !important;
                    max-width: none !important;
                    border-radius: 1.25rem !important;
                    margin-top: 0 !important;
                }
                .employer-jobs-modal-header,
                .employer-jobs-modal-footer {
                    padding: 1rem !important;
                }
                .employer-jobs-modal-body {
                    padding: 1rem !important;
                    max-height: 60vh !important;
                }
                .employer-jobs-modal-body .grid {
                    gap: 0.75rem !important;
                }
                .employer-jobs-modal-footer {
                    flex-direction: column !important;
                    gap: 0.75rem !important;
                }
                .employer-jobs-modal-footer button {
                    width: 100% !important;
                }
            }
        `}</style>
        <div className="min-h-screen bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 employer-jobs-container">
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-6 employer-jobs-h1">My job posts</h1>
                    
                    {/* Navigation Tabs */}
                    <div className="flex space-x-8 border-b border-gray-200 mb-6 employer-jobs-tabs">
                        {[
                            { key: 'all', label: 'All' },
                            { key: 'active', label: 'Active' },
                            { key: 'draft', label: 'Draft' },
                            { key: 'archived', label: 'Archived' }
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === tab.key
                                        ? 'border-gray-900 text-gray-900'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Top Controls */}
                    <div className="flex items-center justify-end mb-6 employer-jobs-controls">
                        {/* Search and New Job Button */}
                        <div className="flex items-center space-x-4 employer-jobs-controls-inner">
                            {/* Search Bar */}
                            <div className="relative employer-jobs-search">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Filter Button */}
                            <button className="p-2 text-gray-500 hover:text-gray-700 employer-jobs-filter-button">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                </svg>
                            </button>

                            {/* New Job Post Button */}
                            <button
                                type="button"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Check if user has no remaining listings (free or paid)
                                    if (credits?.remainingListings <= 0) {
                                        const message = credits?.isFreePlan 
                                            ? 'Your Free Starter job post is already live. Upgrade or purchase a plan to post more roles.'
                                            : 'You have used all of your purchased job listings. Please buy additional listings to post more jobs.';
                                        alert(message);
                                        setShowUpgradeModal(true);
                                        return;
                                    }
                                    setShowNewJobModal(true);
                                    setJobFormStep(2);
                                }}
                                className="inline-flex items-center px-4 py-2 bg-black text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors employer-jobs-new-button"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                New jobs post
                            </button>
                        </div>
                    </div>
                </div>

                {/* Bulk Action Bar */}
                {selectedJobs.length > 0 && (
                    <div className="mb-3 p-2.5 bg-gray-50 border border-gray-200 rounded-lg shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 employer-jobs-bulk">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-900">
                                {selectedJobs.length} job{selectedJobs.length !== 1 ? 's' : ''} selected
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap employer-jobs-bulk-actions">
                            <span className="text-xs text-gray-600 font-medium">Move to:</span>
                            <button
                                onClick={() => updateJobStatus('Posted')}
                                className="px-2.5 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full hover:bg-green-200 transition-colors border border-green-300"
                            >
                                Posted
                            </button>
                            <button
                                onClick={() => updateJobStatus('Draft')}
                                className="px-2.5 py-1 text-xs font-medium bg-white text-gray-900 rounded-full hover:bg-gray-50 transition-colors border border-gray-300"
                            >
                                Draft
                            </button>
                            <button
                                onClick={() => updateJobStatus('Archived')}
                                className="px-2.5 py-1 text-xs font-medium bg-black text-white rounded-full hover:bg-gray-800 transition-colors border border-black"
                            >
                                Archived
                            </button>
                            <button
                                onClick={deleteJobs}
                                className="px-2.5 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full hover:bg-red-200 transition-colors border border-red-300"
                            >
                                Delete
                            </button>
                            <button
                                onClick={() => setSelectedJobs([])}
                                className="px-2.5 py-1 text-xs font-medium bg-white text-gray-700 rounded-full hover:bg-gray-50 transition-colors border border-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Job Posts Table */}
                {filteredJobs.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center employer-jobs-empty">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No job posts found</h3>
                        <p className="text-gray-600 mb-8 max-w-md mx-auto">
                            {searchTerm ? 'No jobs match your search criteria.' : 'Create your first job post to start attracting qualified candidates.'}
                        </p>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowNewJobModal(true);
                                setJobFormStep(2);
                            }}
                            className="inline-flex items-center px-8 py-4 bg-black text-white font-medium rounded-full hover:bg-gray-800 transition-colors"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Create Your First Job Post
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden employer-jobs-table">
                        {/* Table Header */}
                        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 employer-jobs-table-header">
                            <div
                                className="grid gap-4 text-sm font-medium text-gray-700"
                                style={{ gridTemplateColumns: jobsTableGridTemplate }}
                            >
                                <div>
                                    <input
                                        type="checkbox"
                                        checked={selectedJobs.length === filteredJobs.length && filteredJobs.length > 0}
                                        onChange={handleSelectAll}
                                        className="rounded border-gray-300 accent-black focus:ring-black"
                                    />
                                </div>
                                <div>Job name</div>
                                <div>Status</div>
                                <div>Type</div>
                                <div>Candidates</div>
                                <div>Location</div>
                                <div>Published</div>
                            </div>
                        </div>

                        {/* Table Body */}
                        <div className="divide-y divide-gray-200">
                            {filteredJobs.map((job) => (
                                <div 
                                    key={job.id} 
                                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors employer-jobs-table-row"
                                    onClick={() => handleJobClick(job.id)}
                                >
                                    <div
                                        className="grid gap-4 items-center employer-jobs-table-row-grid"
                                        style={{ gridTemplateColumns: jobsTableGridTemplate }}
                                    >
                                        {/* Checkbox */}
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedJobs.includes(job.id)}
                                                onChange={() => handleSelectJob(job.id)}
                                                className="rounded border-gray-300 accent-black focus:ring-black"
                                            />
                                        </div>

                                        {/* Job Name */}
                                        <div>
                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                {job.title}
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="employer-jobs-row-meta">
                                            {getStatusBadge(job.status)}
                                        </div>

                                        {/* Type */}
                                        <div>
                                            {getTypeBadge(job.employment_type)}
                                        </div>

                                        {/* Candidates */}
                                        <div 
                                            className="cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewApplications(job.id);
                                            }}
                                        >
                                            <div className="flex items-center employer-jobs-applicants">
                                                <div className="flex -space-x-2">
                                                    {(() => {
                                                        const candidateImages = job.candidateImages || [];
                                                        const displayCount = Math.min(3, candidateImages.length);
                                                        const placeholderCount = 3 - displayCount;
                                                        
                                                        return (
                                                            <>
                                                                {candidateImages.slice(0, displayCount).map((candidate, i) => (
                                                                    <div 
                                                                        key={i} 
                                                                        className="w-8 h-8 rounded-full border-2 border-white overflow-hidden bg-gray-200 flex items-center justify-center"
                                                                    >
                                                                        {candidate.imageUrl ? (
                                                                            <img 
                                                                                src={candidate.imageUrl} 
                                                                                alt="" 
                                                                                className="w-full h-full object-cover"
                                                                                onError={(e) => {
                                                                                    e.target.style.display = 'none';
                                                                                    e.target.nextSibling.style.display = 'flex';
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                        <span 
                                                                            className="text-xs font-medium text-gray-600"
                                                                            style={{ display: candidate.imageUrl ? 'none' : 'flex' }}
                                                                        >
                                                                            {candidate.initials}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                                {[...Array(placeholderCount)].map((_, i) => (
                                                                    <div key={`placeholder-${i}`} className="w-8 h-8 bg-gray-200 rounded-full border-2 border-white"></div>
                                                                ))}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                                <span className="ml-2 text-sm text-gray-900 font-medium bg-gray-100 px-2 py-1 rounded-full">
                                                    {applicationsCountByJob[job.id] || 0}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Location */}
                                        <div>
                                            <div className="flex items-center">
                                                <span className="text-sm text-gray-600">{getLocationFlag(job.location)}</span>
                                                <span className="ml-1 text-sm text-gray-600">{job.location}</span>
                                            </div>
                                        </div>

                                        {/* Published */}
                                        <div>
                                            <span className="text-sm text-gray-600">
                                                {formatDate(job.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {showPromotionModal && (
                <>
                <style>{`
                    @media (max-width: 1023px) {
                        .promotion-modal-free-post {
                            min-height: 200px !important;
                            display: flex !important;
                            flex-direction: column !important;
                            justify-content: center !important;
                        }
                    }
                    @media (max-width: 767px) {
                        .promotion-modal-free-post {
                            min-height: 180px !important;
                        }
                    }
                `}</style>
                <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setShowPromotionModal(false)}
                    />
                    <div className="relative w-full max-w-4xl bg-white rounded-xl sm:rounded-2xl md:rounded-[32px] shadow-2xl overflow-hidden px-3 py-4 sm:px-4 sm:py-6 md:px-8 md:pt-8 md:pb-10 my-2 sm:my-auto max-h-[95vh] sm:max-h-none overflow-y-auto">
                        <div className="flex items-start justify-between mb-3 sm:mb-0">
                            <div>
                                <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">
                                    New job Posting
                                </h2>
                            </div>
                            <button
                                onClick={() => setShowPromotionModal(false)}
                                className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 flex-shrink-0 -mt-1"
                                aria-label="Close"
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="mt-3 sm:mt-4 md:mt-6 text-center">
                            <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-900 px-1 sm:px-2 leading-tight">
                                Promote your job post to get 3x more qualified applicants
                            </h3>
                        </div>
                        <div className="mt-4 sm:mt-6 md:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedPromotionPlan('free')}
                                onKeyDown={(e) => e.key === 'Enter' && setSelectedPromotionPlan('free')}
                                className={`promotion-modal-free-post rounded-xl sm:rounded-2xl md:rounded-[28px] px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-8 bg-white cursor-pointer transition-all ${
                                    selectedPromotionPlan === 'free'
                                        ? 'border-2 border-gray-900 shadow-lg ring-2 ring-gray-900/10'
                                        : 'border border-gray-200 hover:border-gray-400'
                                }`}
                            >
                                <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3 md:mb-4">Free post</p>
                                <ul className="space-y-1.5 sm:space-y-2 md:space-y-3 text-xs sm:text-sm md:text-base text-gray-700">
                                    <li className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
                                        <span className="text-gray-900 text-sm sm:text-base md:text-lg flex-shrink-0">✓</span>
                                        <span>Shown in search results</span>
                                    </li>
                                </ul>
                            </div>
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedPromotionPlan('sponsored')}
                                onKeyDown={(e) => e.key === 'Enter' && setSelectedPromotionPlan('sponsored')}
                                className={`rounded-xl sm:rounded-2xl md:rounded-[32px] px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-8 bg-white cursor-pointer transition-all ${
                                    selectedPromotionPlan === 'sponsored'
                                        ? 'border-2 border-gray-900 shadow-xl ring-2 ring-gray-900/10'
                                        : 'border border-gray-300 hover:border-gray-500 shadow-md'
                                }`}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 md:gap-3 mb-2 sm:mb-3">
                                    <p className="text-xs sm:text-sm font-semibold text-gray-900">Sponsored Jobs</p>
                                    <span className="px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs rounded-full bg-gray-200 text-gray-900 font-semibold uppercase tracking-wide w-fit">
                                        Recommended
                                    </span>
                                </div>
                                <div className="flex items-end gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                    <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">R749</span>
                                    <span className="text-[10px] sm:text-xs md:text-sm text-gray-500 pb-1 sm:pb-2">/ Per Month</span>
                                </div>
                                <p className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-900 mb-2 sm:mb-3 md:mb-4">For companies hiring fast</p>
                                <ul className="space-y-1 sm:space-y-1.5 sm:space-y-2 md:space-y-3 text-xs sm:text-sm md:text-base text-gray-800">
                                    {[
                                        "Premium listing",
                                        "Top-of-search placement",
                                        "Recommended Job matching",
                                        "Job analytics",
                                        "Team collaboration of 5-Seats",
                                    ].map((item) => (
                                        <li key={item} className="flex items-start gap-1.5 sm:gap-2 md:gap-3">
                                            <span className="text-gray-900 text-sm sm:text-base md:text-lg leading-5 sm:leading-6 flex-shrink-0 mt-0.5">✓</span>
                                            <span className="leading-tight">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="mt-4 sm:mt-6 md:mt-10 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 md:gap-4">
                            <button
                                onClick={() => handlePromotionAction(false)}
                                className="w-full sm:w-auto px-4 sm:px-6 md:px-8 py-2.5 sm:py-2.5 md:py-3 rounded-full border border-gray-900 text-gray-900 text-xs sm:text-sm font-semibold hover:bg-gray-50 min-h-[44px] sm:min-h-0"
                            >
                                Post without promoting
                            </button>
                            <button
                                onClick={() => handlePromotionAction(true)}
                                className="w-full sm:w-auto px-4 sm:px-6 md:px-8 py-2.5 sm:py-2.5 md:py-3 rounded-full bg-gray-900 text-white text-xs sm:text-sm font-semibold hover:bg-black min-h-[44px] sm:min-h-0"
                            >
                                Promote job
                            </button>
                        </div>
                    </div>
                </div>
                </>
            )}
            {showUpgradeModal && (
                <>
                <style>{`
                    @media (max-width: 1023px) {
                        .upgrade-modal-content {
                            min-height: 200px !important;
                            display: flex !important;
                            flex-direction: column !important;
                            justify-content: center !important;
                        }
                    }
                    @media (max-width: 767px) {
                        .upgrade-modal-content {
                            min-height: 180px !important;
                        }
                    }
                `}</style>
                <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setShowUpgradeModal(false)}
                    />
                    <div className="relative w-full max-w-4xl bg-white rounded-xl sm:rounded-2xl md:rounded-[32px] shadow-2xl overflow-hidden px-3 py-4 sm:px-4 sm:py-6 md:px-8 md:pt-8 md:pb-10 my-2 sm:my-auto max-h-[95vh] sm:max-h-none overflow-y-auto">
                        <div className="flex items-start justify-between mb-3 sm:mb-0">
                            <div>
                                <p className="text-[10px] sm:text-xs md:text-sm text-gray-500">Step 1 of 3</p>
                                <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">
                                    New job Posting
                                </h2>
                            </div>
                            <button
                                onClick={() => setShowUpgradeModal(false)}
                                className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 flex-shrink-0 -mt-1"
                                aria-label="Close"
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="mt-3 sm:mt-4 md:mt-6">
                            <div className="mb-4 sm:mb-6">
                                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                                    Posting limit reached
                                </h3>
                                <p className="text-sm sm:text-base text-gray-700">
                                    To create a new listing, please sponsor or upgrade your plan to unlock additional job posts.
                                </p>
                            </div>
                            <div className="upgrade-modal-content bg-gray-50 rounded-xl sm:rounded-2xl md:rounded-[28px] px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-8">
                                <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-[32px] px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-8 border border-gray-300 shadow-xl">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 md:gap-3 mb-2 sm:mb-3">
                                        <p className="text-xs sm:text-sm font-semibold text-gray-900">Sponsored Jobs</p>
                                        <span className="px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs rounded-full bg-gray-800 text-white w-fit">
                                            Recommended
                                        </span>
                                    </div>
                                    <div className="flex items-end gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                        <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">R749</span>
                                        <span className="text-[10px] sm:text-xs md:text-sm text-gray-500 pb-1 sm:pb-2">/ Per Month</span>
                                    </div>
                                    <p className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-900 mb-2 sm:mb-3 md:mb-4">For companies hiring fast</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 sm:gap-y-1.5 md:gap-y-2">
                                        <ul className="space-y-1 sm:space-y-1.5 sm:space-y-2 md:space-y-3 text-xs sm:text-sm md:text-base text-gray-800">
                                            {[
                                                "Premium listing",
                                                "Top-of-search placement",
                                                "Recommended Job matching",
                                            ].map((item) => (
                                                <li key={item} className="flex items-start gap-1.5 sm:gap-2 md:gap-3">
                                                    <span className="text-gray-900 text-sm sm:text-base md:text-lg leading-5 sm:leading-6 flex-shrink-0 mt-0.5">✓</span>
                                                    <span className="leading-tight">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <ul className="space-y-1 sm:space-y-1.5 sm:space-y-2 md:space-y-3 text-xs sm:text-sm md:text-base text-gray-800">
                                            {[
                                                "Job analytics",
                                                "Team collaboration of 5-Seats",
                                            ].map((item) => (
                                                <li key={item} className="flex items-start gap-1.5 sm:gap-2 md:gap-3">
                                                    <span className="text-gray-900 text-sm sm:text-base md:text-lg leading-5 sm:leading-6 flex-shrink-0 mt-0.5">✓</span>
                                                    <span className="leading-tight">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 sm:mt-6 md:mt-10 flex justify-end px-3 pb-3 sm:px-4 sm:pb-4 md:px-6 md:pb-6">
                            <button
                                onClick={() => {
                                    setShowUpgradeModal(false);
                                    setShowPurchaseModal(true);
                                }}
                                className="px-4 sm:px-6 md:px-8 py-2.5 sm:py-2.5 md:py-3 rounded-full bg-black text-white text-xs sm:text-sm md:text-base font-medium hover:bg-gray-900 min-h-[44px] sm:min-h-0"
                            >
                                Promote job
                            </button>
                        </div>
                    </div>
                </div>
                </>
            )}
            {showPurchaseModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={handleClosePurchaseModal}
                    />
                    <div className="relative w-full max-w-2xl bg-white rounded-[32px] shadow-2xl overflow-hidden px-6 pt-6 pb-8">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <p className="text-sm text-gray-500">Promote job</p>
                                <h2 className="text-xl font-semibold text-gray-900">Complete payment to sponsor this listing</h2>
                            </div>
                            <button
                                onClick={handleClosePurchaseModal}
                                className="p-2 rounded-full hover:bg-gray-100"
                                aria-label="Close payment modal"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-6">
                            Purchase a Sponsored Job credit to unlock 3x more visibility for your new post.
                        </p>
                        <JobListingPurchasePanel
                            onPurchaseRecorded={async (purchaseData) => {
                                setShowPurchaseModal(false);
                                // Wait a moment for database to commit, then refresh page to update all components
                                setTimeout(() => {
                                    window.location.reload();
                                }, 1500);
                            }}
                        />
                    </div>
                </div>
            )}
            {showNewJobModal && (
                <div className="fixed inset-0 z-50 flex items-start justify-center p-4 employer-jobs-modal-overlay">
                    <div className="absolute inset-0 bg-black/30" onClick={() => {
                        setShowNewJobModal(false);
                        setJobFormStep(2);
                    }} />
                    <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden employer-jobs-modal">
                        <div className="flex items-center justify-between px-6 py-4 border-b employer-jobs-modal-header">
                            {/* modal heading */}
                            <h2 className="text-lg font-semibold employer-jobs-h2">Create new job post</h2>

                            {/* set job as dev mode checkbox */}
                            {user?.email.includes('@fourminutemedicine.com') && (<div className="flex items-center gap-2">
                                <input type="checkbox" id="devMode" className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                checked={newJobForm.is_dev_job}
                                onChange={(e) => setNewJobForm((p) => ({ ...p, is_dev_job: e.target.checked }))}
                                />
                                <label htmlFor="devMode" className="text-sm font-medium text-gray-700">Set job as dev mode (for testing purposes)</label>
                            </div>)}

                            {/* close button */}
                            <button onClick={() => {
                                setShowNewJobModal(false);
                                setJobFormStep(2);
                            }} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close">
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto employer-jobs-modal-body">
                            {/* Step 2: Job Description */}
                            {jobFormStep === 2 && (
                                <div className="space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Job Title*</label>
                                            <input className="rounded-full border border-gray-300 px-4 py-2 w-full" value={newJobForm.jobTitle} onChange={(e) => setNewJobForm((p) => ({ ...p, jobTitle: e.target.value }))} />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Role category*</label>
                                            <select className="rounded-full border border-gray-300 px-4 py-2 w-full" value={newJobForm.specialty} onChange={(e) => setNewJobForm((p) => ({ ...p, specialty: e.target.value }))}>
                                            <option value="">Select a role</option>
                                            {Array.isArray(roleCategories) && roleCategories.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Describe the role?</label>
                                        <textarea rows={3} className="w-full rounded-2xl border border-gray-200 px-4 py-3" placeholder="Provide a brief overview of the position" value={newJobForm.roleDescription} onChange={(e) => setNewJobForm((p) => ({ ...p, roleDescription: e.target.value }))} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Role Requirements?</label>
                                        <textarea 
                                            rows={3} 
                                            className="w-full rounded-2xl border border-gray-200 px-4 py-3" 
                                            placeholder="List the qualifications, skills and experience required for this position."
                                            value={newJobForm.preferredCandidate} 
                                            onChange={(e) => {
                                                let value = e.target.value;
                                                if (value.length === 1 && !value.startsWith('●')) {
                                                    value = '●  ' + value;
                                                }
                                                setNewJobForm((p) => ({ ...p, preferredCandidate: value }));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    const textarea = e.target;
                                                    const start = textarea.selectionStart;
                                                    const end = textarea.selectionEnd;
                                                    const value = newJobForm.preferredCandidate || '';
                                                    const newValue = value.substring(0, start) + '\n●  ' + value.substring(end);
                                                    setNewJobForm((p) => ({ ...p, preferredCandidate: newValue }));
                                                    setTimeout(() => {
                                                        textarea.selectionStart = textarea.selectionEnd = start + 4;
                                                    }, 0);
                                                }
                                            }}
                                            onFocus={(e) => {
                                                if (!newJobForm.preferredCandidate) {
                                                    setNewJobForm((p) => ({ ...p, preferredCandidate: '●  ' }));
                                                }
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Duties and responsibilities?</label>
                                        <textarea 
                                            rows={3} 
                                            className="w-full rounded-2xl border border-gray-200 px-4 py-3" 
                                            placeholder="List day-to-day responsibilities and expectations for this role."
                                            value={newJobForm.dutiesAndResponsibilities} 
                                            onChange={(e) => {
                                                let value = e.target.value;
                                                if (value.length === 1 && !value.startsWith('●')) {
                                                    value = '●  ' + value;
                                                }
                                                setNewJobForm((p) => ({ ...p, dutiesAndResponsibilities: value }));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    const textarea = e.target;
                                                    const start = textarea.selectionStart;
                                                    const end = textarea.selectionEnd;
                                                    const value = newJobForm.dutiesAndResponsibilities || '';
                                                    const newValue = value.substring(0, start) + '\n●  ' + value.substring(end);
                                                    setNewJobForm((p) => ({ ...p, dutiesAndResponsibilities: newValue }));
                                                    setTimeout(() => {
                                                        textarea.selectionStart = textarea.selectionEnd = start + 4;
                                                    }, 0);
                                                }
                                            }}
                                            onFocus={(e) => {
                                                if (!newJobForm.dutiesAndResponsibilities) {
                                                    setNewJobForm((p) => ({ ...p, dutiesAndResponsibilities: '●  ' }));
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Employment Type*</label>
                                            <select className="rounded-full border border-gray-300 px-4 py-2 w-full" value={newJobForm.employmentType} onChange={(e) => setNewJobForm((p) => ({ ...p, employmentType: e.target.value }))}>
                                                <option value="">Select employment type</option>
                                                <option value="full-time">Full-time</option>
                                                <option value="part-time">Part-time</option>
                                                <option value="contract">Contract</option>
                                                <option value="temporary">Temporary</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Work preference*</label>
                                            <select className="rounded-full border border-gray-300 px-4 py-2 w-full" value={newJobForm.workPreference} onChange={(e) => setNewJobForm((p) => ({ ...p, workPreference: e.target.value }))}>
                                                <option value="">Select work preference</option>
                                                <option value="remote">Remote</option>
                                                <option value="on-site">On-site</option>
                                                <option value="hybrid">Hybrid</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Country*</label>
                                            <Select
                                                options={[...countryList().getData()].sort((a, b) => (a.label === 'South Africa' ? -1 : b.label === 'South Africa' ? 1 : a.label.localeCompare(b.label)))}
                                                value={countryList().getData().find(c => c.value === newJobForm.countryCode || c.label === newJobForm.country) || null}
                                                onChange={(selectedOption) => {
                                                    const countryCode = selectedOption?.value || "";
                                                    setNewJobForm((p) => ({ 
                                                        ...p, 
                                                        country: selectedOption?.label || "",
                                                        countryCode: countryCode,
                                                        province: ""
                                                    }));
                                                }}
                                                placeholder="Select country..."
                                                isSearchable
                                                classNamePrefix="react-select"
                                                styles={{
                                                    control: (provided, state) => ({
                                                        ...provided,
                                                        borderRadius: '9999px',
                                                        borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                        boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                        '&:hover': { borderColor: '#9ca3af' },
                                                    }),
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Province/State*</label>
                                            {newJobForm.countryCode ? (
                                                <Select
                                                    options={State.getStatesOfCountry(newJobForm.countryCode).map((s) => ({ value: s.isoCode, label: s.name }))}
                                                    value={newJobForm.province ? { value: newJobForm.province, label: newJobForm.province } : null}
                                                    onChange={(selectedOption) => {
                                                        setNewJobForm((p) => ({ 
                                                            ...p, 
                                                            province: selectedOption?.label || ""
                                                        }));
                                                    }}
                                                    placeholder="Select province/state..."
                                                    isSearchable
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        control: (provided, state) => ({
                                                            ...provided,
                                                            borderRadius: '9999px',
                                                            borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                            boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                            '&:hover': { borderColor: '#9ca3af' },
                                                        }),
                                                    }}
                                                />
                                            ) : (
                                                <input type="text" className="rounded-full border border-gray-300 px-4 py-2 w-full bg-gray-50" value={newJobForm.province} onChange={(e) => setNewJobForm((p) => ({ ...p, province: e.target.value }))} placeholder="Select country first" disabled />
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Language requirement*</label>
                                            <Select
                                                isMulti
                                                options={languages.map(lang => ({ value: lang, label: lang }))}
                                                value={Array.isArray(newJobForm.languageRequirement) ? newJobForm.languageRequirement.map(lang => ({ value: lang, label: lang })) : []}
                                                onChange={(selectedOptions) => {
                                                    setNewJobForm((p) => ({ 
                                                        ...p, 
                                                        languageRequirement: selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                                    }));
                                                }}
                                                placeholder="Select language(s)..."
                                                isSearchable
                                                classNamePrefix="react-select"
                                                styles={{
                                                    control: (provided, state) => ({
                                                        ...provided,
                                                        borderRadius: '9999px',
                                                        borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                        minHeight: '42px',
                                                        '&:hover': { borderColor: '#9ca3af' },
                                                    }),
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <label className="block text-sm font-medium text-gray-700">Location or licence restrictions*</label>
                                            </div>
                                            <div className="flex gap-2 mb-3">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const answer = newJobForm.locationOrLicenceRestrictions === "yes" ? null : "yes";
                                                        setNewJobForm((p) => ({ 
                                                            ...p, 
                                                            locationOrLicenceRestrictions: answer,
                                                            locationOrLicenceRestrictionsText: answer === null ? "" : p.locationOrLicenceRestrictionsText
                                                        }));
                                                    }}
                                                    className={`flex-1 flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                        newJobForm.locationOrLicenceRestrictions === "yes"
                                                            ? 'bg-gray-800 text-white border-gray-800'
                                                            : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    Yes
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const answer = newJobForm.locationOrLicenceRestrictions === "no" ? null : "no";
                                                        setNewJobForm((p) => ({ 
                                                            ...p, 
                                                            locationOrLicenceRestrictions: answer,
                                                            locationOrLicenceRestrictionsText: answer === null ? "" : p.locationOrLicenceRestrictionsText
                                                        }));
                                                    }}
                                                    className={`flex-1 flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                        newJobForm.locationOrLicenceRestrictions === "no"
                                                            ? 'bg-gray-800 text-white border-gray-800'
                                                            : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    No
                                                </button>
                                            </div>
                                            {newJobForm.locationOrLicenceRestrictions === "yes" && (
                                                <div>
                                                    <label className="block text-sm text-gray-700 mb-1">What are the restrictions</label>
                                                    <input 
                                                        type="text"
                                                        className="rounded-full border border-gray-300 px-4 py-2 w-full" 
                                                        value={newJobForm.locationOrLicenceRestrictionsText} 
                                                        onChange={(e) => setNewJobForm((p) => ({ ...p, locationOrLicenceRestrictionsText: e.target.value }))} 
                                                        placeholder="Enter restrictions"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Requirements */}
                            {jobFormStep === 3 && (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Specialty*</label>
                                            <select className="rounded-full border border-gray-300 px-4 py-2 w-full" value={newJobForm.requirementsSpecialty} onChange={(e) => setNewJobForm((p) => ({ ...p, requirementsSpecialty: e.target.value }))}>
                                                <option value="">Select a specialty</option>
                                                {Array.isArray(specialties) && specialties.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Experience level eg. Grade</label>
                                            <select className="rounded-full border border-gray-300 px-4 py-2 w-full" value={newJobForm.experienceLevel} onChange={(e) => setNewJobForm((p) => ({ ...p, experienceLevel: e.target.value }))}>
                                                <option value="">Select experience level</option>
                                                <option value="medical-student">Medical Student</option>
                                                <option value="junior-doctor">Junior Doctor (Intern)</option>
                                                <option value="medical-officer">Medical Officer</option>
                                                <option value="registrar">Registrar</option>
                                                <option value="fellow">Fellow</option>
                                                <option value="specialist">Specialist</option>
                                                <option value="consultant">Consultant</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Budget for role</label>
                                            <div className="flex gap-1">
                                                <select
                                                    className="rounded-full border border-gray-300 px-2 py-2 bg-gray-50 text-sm w-[75px]"
                                                    value={newJobForm.salaryCurrency || "ZAR"}
                                                    onChange={(e) => setNewJobForm((p) => ({ ...p, salaryCurrency: e.target.value }))}
                                                >
                                                    {currencies.map((currency) => (
                                                        <option key={currency.code} value={currency.code}>{currency.code}</option>
                                                    ))}
                                                </select>
                                                <input 
                                                    type="text"
                                                    inputMode="numeric"
                                                    className="rounded-full border border-gray-300 px-3 py-2 flex-1 min-w-0" 
                                                    value={newJobForm.budgetForRole} 
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/[^0-9\s,\-–]/g, '');
                                                        setNewJobForm((p) => ({ ...p, budgetForRole: value }));
                                                    }}
                                                    placeholder="Range"
                                                />
                                                <select
                                                    className="rounded-full border border-gray-300 px-2 py-2 bg-gray-50 text-sm w-[85px]"
                                                    value={newJobForm.salaryPeriod || "yearly"}
                                                    onChange={(e) => setNewJobForm((p) => ({ ...p, salaryPeriod: e.target.value }))}
                                                >
                                                    {salaryPeriods.map((period) => (
                                                        <option key={period.value} value={period.value}>{period.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm text-gray-700 mb-1">Years of Experience</label>
                                            <input type="number" min="0" className="rounded-full border border-gray-300 px-4 py-2 w-full" value={newJobForm.yearsOfExperience} onChange={(e) => setNewJobForm((p) => ({ ...p, yearsOfExperience: e.target.value }))} placeholder="e.g. 3" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-700 mb-1">What skills are required?</label>
                                        <div className="relative">
                                            <input 
                                                className="rounded-full border border-gray-300 px-4 py-2 w-full pr-10" 
                                                value={skillInput} 
                                                onChange={(e) => setSkillInput(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addSkill(skillInput);
                                                    }
                                                }}
                                                placeholder="Enter skill and press Enter"
                                            />
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-400">
                                                    <circle cx="11" cy="11" r="8"/>
                                                    <path d="m21 21-4.35-4.35"/>
                                                </svg>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">'Enter' to add Skills</p>
                                        {newJobForm.requiredSkills.length > 0 && (
                                            <div className="mt-3">
                                                <div className="flex flex-wrap gap-2">
                                                    {newJobForm.requiredSkills.map((skill, index) => (
                                                        <span key={index} className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                                                            {skill}
                                                            <button onClick={() => removeSkill(index)} className="text-gray-500 hover:text-red-600">×</button>
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-700 mb-3">Other preferences</label>
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            <button
                                                type="button"
                                                onClick={() => setNewJobForm((p) => ({ ...p, screeningQuestions: !p.screeningQuestions }))}
                                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                    newJobForm.screeningQuestions 
                                                        ? 'bg-black text-white border-black' 
                                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Screening questions
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newValue = !newJobForm.driversLicenseRequired;
                                                    setNewJobForm((p) => ({ ...p, driversLicenseRequired: newValue }));
                                                    if (!newValue) setDriversLicenseAnswer(null);
                                                }}
                                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                    newJobForm.driversLicenseRequired 
                                                        ? 'bg-black text-white border-black' 
                                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Drivers license required
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newValue = !newJobForm.visaSponsorshipOffered;
                                                    setNewJobForm((p) => ({ ...p, visaSponsorshipOffered: newValue }));
                                                    if (!newValue) setVisaSponsorshipAnswer(null);
                                                }}
                                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                    newJobForm.visaSponsorshipOffered 
                                                        ? 'bg-black text-white border-black' 
                                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Visa sponsorship offered
                                            </button>
                                        </div>
                                        {screeningQuestionsList.length > 0 && (
                                            <div className="mb-4 space-y-3">
                                                <div className="text-sm font-medium text-gray-700 mb-2">Saved Questions:</div>
                                                {screeningQuestionsList.map((savedQuestion, qIndex) => (
                                                    <div key={qIndex} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                                        <div className="flex items-start justify-between mb-2">
                                                            <p className="text-sm font-medium text-gray-900">{savedQuestion.question}</p>
                                                            <button type="button" onClick={() => removeScreeningQuestion(qIndex)} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 ml-2">
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                        {savedQuestion.type === "multiple-choice" && savedQuestion.options && Array.isArray(savedQuestion.options) && (
                                                            <div className="space-y-1 mt-2">
                                                                {savedQuestion.options.map((option, optIndex) => (
                                                                    <div key={optIndex} className="flex items-center gap-2 text-sm text-gray-600">
                                                                        <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-600 flex-shrink-0">
                                                                            {String.fromCharCode(9311 + optIndex)}
                                                                        </div>
                                                                        <span>{option}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {newJobForm.screeningQuestions && (
                                            <div className="mb-4 space-y-3">
                                                <div>
                                                    <input type="text" placeholder="Type your question" value={currentQuestion} onChange={(e) => setCurrentQuestion(e.target.value)} className="w-full rounded-xl border border-gray-300 px-4 py-2 text-sm" />
                                                </div>
                                                
                                                {/* Question Type Buttons - matching image style */}
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setCurrentQuestionType("multiple-choice")}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                            currentQuestionType === "multiple-choice"
                                                                ? 'bg-gray-900 text-white border-gray-900'
                                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        Multi-choice question
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCurrentQuestionType("text")}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                                            currentQuestionType === "text"
                                                                ? 'bg-gray-900 text-white border-gray-900'
                                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        Long answer
                                                    </button>
                                                </div>
                                                
                                                {/* Options input - only show for multi-choice */}
                                                {currentQuestionType === "multiple-choice" && (
                                                    <>
                                                        <div className="space-y-2">
                                                            {currentOptions.map((option, index) => (
                                                                <div key={index} className="flex items-center gap-2">
                                                                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs text-gray-600 flex-shrink-0">
                                                                        {String.fromCharCode(9311 + index)}
                                                                    </div>
                                                                    <input type="text" placeholder={`Option ${index + 1}`} value={option} onChange={(e) => updateScreeningOption(index, e.target.value)} className="flex-1 rounded-xl border border-gray-300 px-4 py-2 text-sm bg-gray-50" />
                                                                    {currentOptions.length > 1 && (
                                                                        <button type="button" onClick={() => removeScreeningOption(index)} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
                                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                            </svg>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <button type="button" onClick={addScreeningOption} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                                                            <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                                </svg>
                                                            </div>
                                                            Add option
                                                        </button>
                                                    </>
                                                )}
                                                
                                                {/* Preview textarea for long answer */}
                                                {currentQuestionType === "text" && (
                                                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                                                        <p className="text-xs text-gray-500 mb-2">Preview</p>
                                                        <textarea
                                                            disabled
                                                            placeholder="Type your answer here..."
                                                            rows={4}
                                                            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm bg-white resize-y cursor-not-allowed opacity-75"
                                                        />
                                                    </div>
                                                )}
                                                
                                                <button type="button" onClick={addScreeningQuestion} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    Add question
                                                </button>
                                            </div>
                                        )}
                                        {newJobForm.driversLicenseRequired && (
                                            <div className="mb-4 space-y-3">
                                                <div className="text-sm font-medium text-gray-900 mb-3">Do you have a driver's licence?</div>
                                                <div className="space-y-2">
                                                    <button type="button" onClick={() => {
                                                        const answer = driversLicenseAnswer === "yes" ? null : "yes";
                                                        setDriversLicenseAnswer(answer);
                                                        setNewJobForm(p => ({ ...p, driversLicenseAnswer: answer }));
                                                    }} className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                        driversLicenseAnswer === "yes" ? 'bg-black text-white border-black' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                                                    }`}>
                                                        Yes
                                                    </button>
                                                    <button type="button" onClick={() => {
                                                        const answer = driversLicenseAnswer === "no" ? null : "no";
                                                        setDriversLicenseAnswer(answer);
                                                        setNewJobForm(p => ({ ...p, driversLicenseAnswer: answer }));
                                                    }} className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                        driversLicenseAnswer === "no" ? 'bg-black text-white border-black' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                                                    }`}>
                                                        No
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {newJobForm.visaSponsorshipOffered && (
                                            <div className="mb-4 space-y-3">
                                                <div className="text-sm font-medium text-gray-900 mb-3">Do you need a visa sponsorship?</div>
                                                <div className="space-y-2">
                                                    <button type="button" onClick={() => {
                                                        const answer = visaSponsorshipAnswer === "yes" ? null : "yes";
                                                        setVisaSponsorshipAnswer(answer);
                                                        setNewJobForm(p => ({ ...p, visaSponsorshipAnswer: answer }));
                                                    }} className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                        visaSponsorshipAnswer === "yes" ? 'bg-black text-white border-black' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                                                    }`}>
                                                        Yes
                                                    </button>
                                                    <button type="button" onClick={() => {
                                                        const answer = visaSponsorshipAnswer === "no" ? null : "no";
                                                        setVisaSponsorshipAnswer(answer);
                                                        setNewJobForm(p => ({ ...p, visaSponsorshipAnswer: answer }));
                                                    }} className={`w-full flex items-center justify-start px-4 py-3 rounded-full border text-sm font-medium transition-colors ${
                                                        visaSponsorshipAnswer === "no" ? 'bg-black text-white border-black' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                                                    }`}>
                                                        No
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <textarea rows={3} className="w-full rounded-2xl border border-gray-200 px-4 py-3" placeholder="Any other preferences or requirements..." value={newJobForm.otherPreferences} onChange={(e) => setNewJobForm((p) => ({ ...p, otherPreferences: e.target.value }))} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t flex items-center gap-3 employer-jobs-modal-footer">
                            <button onClick={() => {
                                setShowNewJobModal(false);
                                setJobFormStep(2);
                            }} className="px-5 py-2 rounded-full border text-sm">Cancel</button>
                            {jobFormStep === 2 ? (
                                <button onClick={handleNextStep} className="ml-auto px-6 py-2 rounded-full bg-black text-white text-sm">
                                    Next step
                                </button>
                            ) : (
                                <>
                                    <button onClick={handleBackStep} className="px-5 py-2 rounded-full border text-sm">Back</button>
                                    <button onClick={createJobListing} disabled={savingNewJob} className="ml-auto px-6 py-2 rounded-full bg-black text-white text-sm disabled:opacity-60">
                                        {savingNewJob ? 'Saving…' : 'Create job'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default EmployerJobs;


